/**
 * Progress — a determinate or indeterminate bar.
 *
 * Determinate is the default and the preferred form; the indeterminate variant
 * exists for the rare operation whose length genuinely is not knowable (an
 * upload behind a proxy). Anything that is merely *loading* gets a Skeleton.
 */

import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export type ProgressVariant =
  | 'accent'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral'
  | 'strong';

const FILL: Record<ProgressVariant, string> = {
  accent: 'var(--accent)',
  info: 'var(--info)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  neutral: 'var(--text-muted)',
  /** The achromatic fill for a quantity that carries no state — see `TRACK`. */
  strong: 'var(--text-primary)',
};

/**
 * The track bounds the geometry; it must never outshout the datum inside it.
 *
 * `--surface-3` (two revisions ago) measured 1.13:1 on dark and 1.12:1 on light
 * — an invisible track, so a 0% bar was nothing at all. Full `--text-muted` (the
 * last revision) overcorrected: at 4.9:1 it was a *heavier* mark than the fill,
 * so an empty bar carried more visual mass than a quarter-full one and the
 * encoding read backwards at the low end. 40% of `--text-muted` over the row
 * lands near 2.5:1 — enough to read as an empty rail, low enough that the
 * `strong` fill clears 4.5:1 against it and remains the loud mark.
 *
 * `sm` is the exception, and it is a different mark. It is the in-table
 * micro-bar that annotates a figure already printed beside it, so a track that
 * reads at all gives a 0% row the same visual weight as a 100% one — 55 of them
 * in one table draw 55 full-width rules that say nothing. It grooves on
 * `--surface-inset` (1.07:1 dark / 1.16:1 light), the treatment the ranked bar
 * lists already use, so length alone carries the value. Pass `track` to
 * override either default.
 */
const TRACK: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'var(--surface-inset)',
  md: 'color-mix(in oklab, var(--text-muted) 40%, transparent)',
  lg: 'color-mix(in oklab, var(--text-muted) 40%, transparent)',
};

/** 2px cannot carry a radius and disappears on a non-retina panel; 4px is the floor. */
const HEIGHT: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-[var(--space-1)]',
  md: 'h-1',
  lg: 'h-1.5',
};

export interface ProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 0–1. Clamped, so a runaway count can never overflow the track. */
  value?: number;
  variant?: ProgressVariant;
  size?: 'sm' | 'md' | 'lg';
  indeterminate?: boolean;
  /** Groove colour. Defaults per `size` — see `TRACK`. */
  track?: string;
  /**
   * Fill colour, overriding `variant`'s — the counterpart of `track`, and for
   * the same reason it exists.
   *
   * A `sm` micro-bar annotates a figure already printed beside it, so sixty of
   * them at full chroma carry more ink than the numbers they are annotating.
   * A caller that draws a whole column of them passes a tint here and restores
   * full strength on the row under the pointer. A CSS variable is a legal value,
   * which is how the hover swap happens without an inline style fighting a
   * class.
   */
  fill?: string;
  /** Announced by assistive tech in place of the raw percentage. */
  valueLabel?: string;
  'aria-label'?: string;
}

export const Progress = forwardRef<HTMLDivElement, ProgressProps>(function Progress(
  {
    value = 0,
    variant = 'accent',
    size = 'md',
    indeterminate = false,
    track,
    fill: fillOverride,
    valueLabel,
    className,
    ...rest
  },
  ref,
) {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
  const fill = fillOverride ?? FILL[variant];
  const groove = track ?? TRACK[size];

  return (
    <div
      ref={ref}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : Math.round(clamped * 100)}
      aria-valuetext={valueLabel ?? (indeterminate ? 'Working…' : undefined)}
      className={cn('w-full overflow-hidden rounded-[var(--radius-xs)]', HEIGHT[size], className)}
      {...rest}
      style={{ background: groove, ...rest.style }}
    >
      {indeterminate ? (
        <div
          className="h-full w-full rounded-[var(--radius-xs)]"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${fill} 50%, transparent 100%)`,
            backgroundSize: '200% 100%',
            animation: 'af-shimmer calc(var(--dur-slow)*4) var(--ease-in-out) infinite',
          }}
        />
      ) : (
        <div
          className="h-full rounded-[var(--radius-xs)]"
          style={{
            width: `${clamped * 100}%`,
            // A 1% rate must still draw a cap rather than nothing at all.
            minWidth: clamped > 0 ? 'var(--radius-xs)' : undefined,
            background: fill,
            transition: 'width var(--dur-normal) var(--ease-out)',
          }}
        />
      )}
    </div>
  );
});
